import React, { useState, useEffect, useCallback } from "react"
import { Modal, Spinner } from 'react-bootstrap'
import { Search } from "lucide-react"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import { fetchKeywordSuggestions, normalizeKeywordItems } from '../../../utils/keywordSuggestionApi';
import './SuggestedKeywordsModal.css'

const SuggestedKeywordsModal = ({
  isOpen,
  toggle,
  itemId,
  selectedCountry,
  selectedLanguage,
  onAddKeywords,
  trackedKeywords = [],
  keywordCount = 0,
  keywordCountLoading = false,
  keywordLimit = 'Unlimited',
  onUpgrade,
}) => {
  const [keywords, setKeywords] = useState([])
  const [selectedKeywords, setSelectedKeywords] = useState([])
  const [loading, setLoading] = useState(false)
  const [addingKeywords, setAddingKeywords] = useState(false)

  const isUnlimited = keywordLimit === 'Unlimited'
  const limitNum = isUnlimited ? Infinity : parseInt(keywordLimit)
  const remainingSlots = isUnlimited ? Infinity : Math.max(0, limitNum - keywordCount)
  // While the real tracked-keyword count is still loading (e.g. a fresh
  // browser session with no local cache yet), `keywordCount` defaults to 0
  // and would make it look like the full plan is available. Don't let that
  // stale/incomplete count decide whether a selection exceeds the limit.
  const wouldExceedLimit = !isUnlimited && !keywordCountLoading && selectedKeywords.length > remainingSlots

  const fetchSuggestedKeywords = useCallback(async () => {
    if (!itemId) return

    setLoading(true)
    try {
      const data = await fetchKeywordSuggestions(itemId)
      const validKeywords = normalizeKeywordItems(data?.keywords)

      if (validKeywords.length > 0) {
        setKeywords(validKeywords.map(item => item.keyword))
      } else if (data?.status === "processing") {
        toastr.info("Still generating keywords for this app - please try again in a moment.")
        setKeywords([])
      } else {
        toastr.info("No suggested keywords found for this app.")
        setKeywords([])
      }
    } catch (error) {
      console.error("Error fetching suggested keywords:", error)
      toastr.error("Failed to fetch suggested keywords.")
      setKeywords([])
    } finally {
      setLoading(false)
    }
  }, [itemId])

  useEffect(() => {
    if (isOpen && itemId) {
      fetchSuggestedKeywords()
    }
  }, [isOpen, itemId, fetchSuggestedKeywords])

  const toggleKeywordSelection = (keyword) => {
    if (trackedKeywords.includes(keyword)) return
    setSelectedKeywords(prev =>
      prev.includes(keyword) ? prev.filter(k => k !== keyword) : [...prev, keyword]
    )
  }

  const toggleSelectAll = () => {
    const untrackedKeywords = keywords.filter(keyword => !trackedKeywords.includes(keyword))
    if (selectedKeywords.length === untrackedKeywords.length) {
      setSelectedKeywords([])
    } else {
      setSelectedKeywords([...untrackedKeywords])
    }
  }

  const handleAddSelectedKeywords = async () => {
    if (selectedKeywords.length === 0) {
      toastr.warning("Please select at least one keyword.")
      return
    }

    if (!selectedCountry || !selectedLanguage) {
      toastr.error("Please select country and language first.")
      return
    }

    if (wouldExceedLimit) {
      if (onUpgrade) onUpgrade()
      return
    }

    setAddingKeywords(true)

    try {
      await onAddKeywords(selectedKeywords)
      setSelectedKeywords([])
      toastr.success(`Successfully added ${selectedKeywords.length} keywords!`)
      toggle()
    } catch (error) {
      console.error("Error adding keywords:", error)
      toastr.error("Failed to add keywords.")
    } finally {
      setAddingKeywords(false)
    }
  }

  return (
    <Modal
      show={isOpen}
      onHide={toggle}
      centered
      size="lg"
      className="modal_key suggested-keywords-modal"
    >
      <div className="suggested-keywords-content">
        {/* Header */}
        <div className="modal-header-custom">
          <div className="header-content">
            <div className="header-icon">
              <Search size={24} />
            </div>
            <div>
              <h5>Suggested Keywords</h5>
              <small className="text-muted">Select keywords to add to your tracking list</small>
            </div>
          </div>
        </div>

        {/* Plan limit banner */}
        {!isUnlimited && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 16px',
            background: '#1a1f2e',
            borderBottom: '1px solid #3A3B47',
            fontSize: '13px',
          }}>
            <i className="fas fa-info-circle" style={{ color: '#9ca3af', flexShrink: 0 }} />
            {keywordCountLoading ? (
              <span style={{ color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Spinner animation="border" size="sm" style={{ color: '#9ca3af' }} />
                Checking how many of your <strong style={{ color: '#fff' }}>{keywordLimit}</strong> keyword slots are already in use…
              </span>
            ) : (
              <span style={{ color: '#9ca3af' }}>
                Your plan allows <strong style={{ color: '#fff' }}>{keywordLimit}</strong> keywords —&nbsp;
                <strong style={{ color: remainingSlots === 0 ? '#ef4444' : '#22c55e' }}>{remainingSlots}</strong> slot{remainingSlots !== 1 ? 's' : ''} remaining.
                {remainingSlots === 0
                  ? ' Selecting more will prompt an upgrade.'
                  : ` Selecting more than ${remainingSlots} will prompt an upgrade.`}
              </span>
            )}
          </div>
        )}

        {/* Body */}
        <div className="modal-body-custom">
          {loading ? (
            <div className="loading-state">
              <Spinner animation="border" variant="primary" />
              <p>Loading suggested keywords...</p>
            </div>
          ) : keywords.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <Search size={32} />
              </div>
              <h6>No Suggested Keywords</h6>
              <p>No suggested keywords available for this app.</p>
            </div>
          ) : (
            <>
              {/* Selection Controls */}
              <div className="selection-controls" style={{ flexWrap: 'wrap', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    checked={keywords.filter(k => !trackedKeywords.includes(k)).length > 0 && selectedKeywords.length === keywords.filter(k => !trackedKeywords.includes(k)).length}
                    onChange={toggleSelectAll}
                    style={{ width: 16, height: 16, accentColor: '#F6255A', cursor: 'pointer' }}
                  />
                  <span style={{ color: '#fff', fontSize: '14px' }}>Select All</span>
                </label>
                <span className="selection-count">
                  {selectedKeywords.length} of {keywords.filter(keyword => !trackedKeywords.includes(keyword)).length} available selected
                </span>
              </div>

              {/* Keywords List */}
              <div className="keywords-list">
                {keywords.map((keyword, index) => {
                  const isSelected = selectedKeywords.includes(keyword)
                  const isTracked = trackedKeywords.includes(keyword)
                  return (
                    <div
                      key={index}
                      className={`keyword-item ${isSelected ? 'selected' : ''} ${isTracked ? 'tracked' : ''}`}
                      onClick={() => { if (!isTracked) toggleKeywordSelection(keyword) }}
                      style={{ cursor: isTracked ? 'not-allowed' : 'pointer' }}
                    >
                      <div className="keyword-content">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: isTracked ? 'not-allowed' : 'pointer', userSelect: 'none', width: '100%' }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isTracked}
                            onChange={() => {
                              if (!isTracked) toggleKeywordSelection(keyword)
                            }}
                            onClick={(e) => {
                              // Only stop this click from also bubbling up to
                              // the row's own onClick (which would toggle a
                              // second time and cancel this one out) — must
                              // NOT preventDefault, or the browser never
                              // flips the checkbox's native checked state and
                              // onChange never fires, making the checkbox
                              // itself a dead click target.
                              e.stopPropagation()
                            }}
                            style={{
                              width: 16,
                              height: 16,
                              accentColor: isTracked ? '#6b7280' : '#F6255A',
                              cursor: isTracked ? 'not-allowed' : 'pointer',
                              opacity: isTracked ? 0.5 : 1
                            }}
                          />
                          <div className="keyword-info" onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            if (!isTracked) {
                              toggleKeywordSelection(keyword)
                            }
                          }}>
                            <div className="keyword-name">{keyword}</div>
                            {isTracked && (
                              <div className="tracked-indicator">
                                <span className="tracked-badge">Already Tracked</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer-custom">
          <button
            className="btn-cancel"
            onClick={toggle}
            disabled={addingKeywords}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleAddSelectedKeywords}
            disabled={selectedKeywords.length === 0 || addingKeywords || keywordCountLoading}
            title={keywordCountLoading ? "Still checking your current keyword count — try again in a moment" : undefined}
            style={wouldExceedLimit ? { background: '#F6255A', borderColor: '#F6255A' } : {}}
          >
            {addingKeywords ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Adding...
              </>
            ) : wouldExceedLimit ? (
              <>
                <i className="fas fa-lock me-2" style={{ fontSize: '12px' }} />
                Upgrade to Add {selectedKeywords.length} Keyword{selectedKeywords.length !== 1 ? 's' : ''}
              </>
            ) : selectedKeywords.length > 0 ? (
              <>Add {selectedKeywords.length} Keyword{selectedKeywords.length !== 1 ? 's' : ''}</>
            ) : (
              <>Select Keywords</>
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default SuggestedKeywordsModal
